import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, CheckCircle2, Plus, Trash2, ShieldAlert, Award, AlertTriangle, Cloud, Check } from 'lucide-react';
import FotoUpload from './FotoUpload';
import FotosOutros from './FotosOutros';
import OverlayProgresso from './OverlayProgresso';
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';
// Seletor de peça da Central de Pedidos (compartilhado com a Etapa 1 desde 2026-07-20).
import SelecaoPecaCentral from './SelecaoPecaCentral';
import useEhCompacto from '../../../utils/useEhCompacto';
import { itemNaoExiste } from './blocosAusentes';

// =====================================================================
// Certificação — Etapa 2. Carrega o diagnóstico (resumo) e coleta ações,
// peças (R$), limpeza, os 9 testes operacionais, calibração, pendências,
// fotos DEPOIS, parecer e aceite. O RESULTADO da certificação é calculado
// no servidor (motor) ao "Concluir". Suporta salvar rascunho (Em execução).
// =====================================================================

const API = '/api/manutencao/certificacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const corSaude = (c) => ({
  Excelente: '#059669', 'Muito Bom': '#4f46e5', Bom: '#65a30d', 'Atenção': '#d97706', 'Crítico': '#dc2626',
}[c] || '#64748b');
const corResultado = (r) => ({
  CERTIFICADO: '#059669', 'CERTIFICADO COM RESTRIÇÕES': '#d97706', 'NÃO CERTIFICADO': '#dc2626',
}[r] || '#64748b');

// Rótulos/opções da classificação de componente (mesmo padrão do diagnóstico).
const CLASSIF_LABEL = { 2: 'Conforme', 1: 'Desgaste', 0: 'Crítico' };
const CLASSIF_OPCOES = [
  { v: 2, t: 'Conforme', c: '#047857', bg: '#d1fae5' },
  { v: 1, t: 'Desgaste', c: '#b45309', bg: '#fef3c7' },
  { v: 0, t: 'Crítico', c: '#b91c1c', bg: '#fee2e2' },
];

// Botões de resultado (testes e medições). Largura FIXA e grade de 4 colunas —
// é o que mantém as linhas alinhadas entre itens que aceitam N/A e os que não
// aceitam; com flex, cada rótulo tinha um tamanho e as colunas dançavam.
const LARGURA_OPCAO = 88;
const COR_OPCAO = {
  Aprovado: ['#059669', '#d1fae5'],
  Ajustado: ['#b45309', '#fef3c7'], // corrigido na visita: passa, mas com rastro
  Pendente: ['#b91c1c', '#fee2e2'],
  Reprovado: ['#b91c1c', '#fee2e2'],
  'N/A': ['#475569', '#e2e8f0'],
};
const estiloOpcao = (sel, res) => {
  const [cor, bg] = COR_OPCAO[res] || ['#475569', '#e2e8f0'];
  return {
    padding: '6px 4px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    border: sel ? `2px solid ${cor}` : '1px solid #cbd5e1',
    background: sel ? bg : '#fff', color: sel ? cor : '#64748b',
    whiteSpace: 'nowrap',
  };
};

// Motivo da troca de peça (chips fixos) e itens fixos da limpeza técnica.
const MOTIVOS_PECA = ['Desgaste', 'Preventiva', 'Falha'];
const LIMPEZA_ITENS = [
  'Limpeza interna completa', 'Limpeza da caixa elétrica', 'Remoção de gordura acumulada',
  'Limpeza do sistema de filtragem', 'Limpeza externa', 'Reaperto de conexões elétricas',
];
// Texto padrão do parecer técnico (o consultor complementa as observações).
/*const PARECER_PADRAO = `O equipamento foi submetido ao Programa de Certificação de Equipamentos do Grupo Correia, contemplando inspeção técnica, recuperação preventiva, substituição de componentes, limpeza técnica e testes operacionais.
Após a conclusão das atividades descritas neste relatório, o equipamento encontra-se apto para operação dentro dos padrões estabelecidos pelo Grupo Correia.

Observações Complementares:
`*/;

const inputStyle = { width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#64748b' };
// Campo preenchido pelo catálogo da central — só leitura (troca-se a peça, não o texto).
const inputTravado = { ...inputStyle, background: '#f8fafc', color: '#334155' };
const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer' };

const Secao = ({ titulo, sub, descricao, children, acao }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: descricao ? 8 : 12 }}>
      <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#475569' }}>
        {titulo} {sub && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>· {sub}</span>}
      </h4>
      {acao}
    </div>
    
    {/* Nova área para textos longos com quebra de linha preservada */}
    {descricao && (
      <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#64748b', whiteSpace: 'pre-line', lineHeight: '1.5' }}>
        {descricao}
      </p>
    )}
    
    {children}
  </div>
);
const btnAdd = (onClick, label) => (
  <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
    <Plus size={12} /> {label}
  </button>
);
const btnDel = (onClick) => (
  <button onClick={onClick} title="Remover" style={{ padding: 6, background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, cursor: 'pointer' }}><Trash2 size={12} /></button>
);

const CertificacaoCertificar = ({ idRelatorio, onVoltar }) => {
  const [base, setBase] = useState(null);     // GET /:id

  // Item (teste ou medição) que não se aplica a ESTE equipamento, pelo que o
  // diagnóstico declarou no cabeçalho: tipo de energia e nº de platens (Chapa)
  // ou as quantidades de sabor/sistema/torre/válvula (Bebidas). Definido aqui em
  // cima porque o `salvar` também precisa — senão o "Concluir" cobraria resposta
  // de linha que a tela nem exibe, travando sem mostrar onde. Ver blocosAusentes.js.
  const ausenteNoEquipamento = (nome) => itemNaoExiste(nome, {
    tipoEnergia: base?.relatorio?.TIPO_ENERGIA,
    qtdPlatens: base?.relatorio?.QTD_PLATENS,
    quantidades: base?.relatorio?.QUANTIDADES,
  });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [concluindo, setConcluindo] = useState(false); // true = finalização (gera o certificado em PDF)
  const [resultado, setResultado] = useState(null);

  // Estado dos formulários da Etapa 2
  const [acoes, setAcoes] = useState([]);
  const [pecas, setPecas] = useState([]);
  const [limpeza, setLimpeza] = useState([]);
  const [testeRes, setTesteRes] = useState({}); // nome → 'Aprovado'|'Pendente'|'N/A'
  const [testeObs, setTesteObs] = useState({}); // nome → observação (preenchida quando 'Pendente')
  // Calibração: bloco único e fixo (temp programada/medida/desvio + resultado).
  const [calib, setCalib] = useState({ temp_programada: '', temp_medida: '', desvio: '', aprovado: null });
  // Medições declaradas na versão do template (chapa): nome → { valor, resultado }.
  // Fica vazio nas versões sem medições (fritadeira), que seguem no bloco único acima.
  const [medVals, setMedVals] = useState({});
  // Versões de software/placas lidas no equipamento — texto livre, sem validação.
  const [versoesSw, setVersoesSw] = useState([]);
  const [pendencias, setPendencias] = useState([]);
  const [fotosDepois, setFotosDepois] = useState({}); // slot nomeado → url
  const [fotosDepoisOutros, setFotosDepoisOutros] = useState([]); // até 3 fotos livres "Outros"
  const [parecer, setParecer] = useState('');
  const [riscoEsp, setRiscoEsp] = useState(false);
  const [dias, setDias] = useState(''); // dias de serviço (resumo executivo)
  const [aceite, setAceite] = useState({ nome: '', data: '' });
  // Reavaliação pós-conserto: id_componente → nova classificação (0/1/2).
  // Só os componentes que saíram Desgaste/Crítico no diagnóstico entram aqui.
  const [reaval, setReaval] = useState({});
  const ehCompacto = useEhCompacto(); // celular/tablet: empilha os campos das peças

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}/${idRelatorio}`, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao carregar.');
      setBase(d);
      // Pré-popula (retomar rascunho)
      setAcoes((d.acoes || []).map((a) => ({ descricao: a.DESCRICAO || '' })));
      // Peças substituídas: se já houver rascunho salvo, usa ele; senão, pré-carrega
      // a partir das "peças a comprar" do diagnóstico (R$ em branco, a confirmar).
      // ORIGEM NULL = relatório anterior à feature → trata como 'Fora'. Família vem
      // do JOIN com o catálogo (apoio visual — não é gravada na peça).
      if ((d.pecas || []).length > 0) {
        setPecas(d.pecas.map((p) => ({
          origem: p.ORIGEM === 'Central' ? 'Central' : 'Fora', id_catalogo: p.ID_CATALOGO || null,
          codigo: p.CODIGO || '', fornecedor: p.FORNECEDOR || '', familia: p.CATALOGO_FAMILIA || '',
          descricao: p.DESCRICAO || '', quantidade: p.QUANTIDADE, motivo: p.MOTIVO || '', valor: p.VALOR,
        })));
      } else {
        // Vindo do diagnóstico: carrega origem/catálogo junto (peça Central no
        // diagnóstico entra como Central na substituição) — só o R$ fica a confirmar.
        setPecas((d.pecasComprar || []).map((p) => ({
          origem: p.ORIGEM === 'Central' ? 'Central' : 'Fora', id_catalogo: p.ID_CATALOGO || null,
          codigo: p.CODIGO || '', fornecedor: p.FORNECEDOR || '', familia: p.CATALOGO_FAMILIA || '',
          descricao: p.DESCRICAO || '', quantidade: p.QUANTIDADE || 1, motivo: p.MOTIVO || '', valor: '',
        })));
      }
      // Limpeza: lista FIXA de atividades; marca "feito" conforme o que já foi salvo.
      const limpSalvo = {};
      (d.limpeza || []).forEach((l) => { limpSalvo[l.ITEM] = !!l.FEITO; });
      setLimpeza(LIMPEZA_ITENS.map((item) => ({ item, feito: limpSalvo[item] || false })));
      const tr = {}; const to = {};
      (d.testes || []).forEach((t) => {
        // Legado: o antigo 'Reprovado' agora se chama 'Pendente'.
        tr[t.TESTE] = t.RESULTADO === 'Reprovado' ? 'Pendente' : t.RESULTADO;
        if (t.OBSERVACAO) to[t.TESTE] = t.OBSERVACAO;
      });
      setTesteRes(tr);
      setTesteObs(to);
      // Calibração: 1 bloco fixo (pega a 1ª linha salva, se houver).
      // As colunas viraram genéricas em 2026-07-22 (VALOR_REFERENCIA/VALOR_MEDIDO/
      // RESULTADO) porque a tabela passou a guardar também GAP e pressão da chapa.
      // Aqui, na tela da fritadeira, elas seguem sendo temperatura em °C.
      // 'Ajustado' conta como aprovado (foi corrigido durante a visita).
      const c0 = (d.calibracoes || [])[0];
      setCalib(c0
        ? {
          temp_programada: c0.VALOR_REFERENCIA ?? '',
          temp_medida: c0.VALOR_MEDIDO ?? '',
          desvio: c0.DESVIO ?? '',
          aprovado: c0.RESULTADO == null ? null : (c0.RESULTADO === 'Aprovado' || c0.RESULTADO === 'Ajustado'),
        }
        : { temp_programada: '', temp_medida: '', desvio: '', aprovado: null });
      // Medições do template: casam pela IDENTIFICACAO, que é o nome cadastrado.
      // Medição não gravada = não se aplica (N/A não vira linha — ver payload).
      const porNome = {};
      (d.calibracoes || []).forEach((c) => {
        if (c.IDENTIFICACAO) porNome[c.IDENTIFICACAO] = { valor: c.VALOR_MEDIDO ?? '', resultado: c.RESULTADO || null };
      });
      setMedVals(porNome);
      setVersoesSw((d.versoesSw || []).map((v) => ({
        item: v.ITEM || '', posicao: v.POSICAO || '', valor: v.VALOR || '',
      })));
      setPendencias((d.pendencias || []).map((p) => ({ item: p.ITEM || '', recomendacao: p.RECOMENDACAO || '', prazo: p.PRAZO || '' })));
      // Separa as fotos DEPOIS: slots "Outros N" vão para a galeria livre; o
      // resto são os slots nomeados (Vista Frontal, …, Etiqueta/Patrimônio).
      const fd = {};
      const outros = [];
      (d.fotos || []).filter((f) => f.MOMENTO === 'depois').forEach((f) => {
        if (/^Outros\s/i.test(f.SLOT || '')) outros.push(f.FOTO_URL);
        else fd[f.SLOT] = f.FOTO_URL;
      });
      setFotosDepois(fd);
      setFotosDepoisOutros(outros);
      // Parecer: o texto-padrão aparece como descrição fixa da seção; aqui só
      // o que foi efetivamente escrito/salvo (observações complementares).
      setParecer(d.relatorio.PARECER_TECNICO || '');
      setRiscoEsp(!!d.relatorio.RISCO_ESPECIALISTA);
      setDias(d.relatorio.DIAS_SERVICO ?? '');
      // Reavaliação: pré-preenche cada componente Desgaste/Crítico com o estado
      // pós-conserto já salvo (CLASSIFICACAO_POS/OBSERVACAO_POS) ou, na 1ª vez, com o diagnóstico.
      const rv = {};
      (d.componentes || []).forEach((c) => {
        if (c.CLASSIFICACAO < 2) rv[c.ID_COMPONENTE] = {
          classificacao: c.CLASSIFICACAO_POS != null ? c.CLASSIFICACAO_POS : c.CLASSIFICACAO,
          observacao: c.OBSERVACAO_POS || '',
        };
      });
      setReaval(rv);
      // Aceite: nome (Gerente de Negócios) e data vêm automáticos (pode editar).
      const hoje = new Date().toISOString().slice(0, 10);
      setAceite({
        nome: d.relatorio.ACEITE_NOME || d.relatorio.GERENTE_NEGOCIOS || '',
        data: d.relatorio.ACEITE_DATA ? String(d.relatorio.ACEITE_DATA).slice(0, 10) : hoje,
      });
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [idRelatorio]);

  useEffect(() => { carregar(); }, [carregar]);

  // Monta o corpo do POST /etapa2/:id. Fonte ÚNICA do payload — usada pelo botão
  // (rascunho manual / conclusão) e pelo autosave, para não divergirem.
  const montarPayloadEtapa2 = (finalizar) => {
    const testesOperacionais = base?.config?.testesOperacionais || [];
    // Calibração vira 1 bloco (ou nada, se totalmente vazia num rascunho).
    const calibPreenchida = calib.aprovado !== null || calib.temp_programada !== '' || calib.temp_medida !== '' || calib.desvio !== '';
    // Medições. Duas formas, conforme a versão do template:
    //  - COM medições declaradas (chapa): uma linha por medição respondida.
    //    Marcar N/A simplesmente NÃO gera linha — medição que não se aplica não
    //    é registrada. Isso evita ter de ensinar 'N/A' ao enum do resultado e à
    //    checagem do motor, que trata toda medição gravada como valendo.
    //  - SEM medições (fritadeira): o bloco único de calibração de temperatura.
    const medicoesTpl = (base?.config?.medicoesTemplate || []).filter((m) => !ausenteNoEquipamento(m.MEDICAO));
    const calibracoesPayload = medicoesTpl.length > 0
      ? medicoesTpl
        .filter((m) => medVals[m.MEDICAO]?.resultado && medVals[m.MEDICAO].resultado !== 'N/A')
        .map((m, i) => {
          const v = medVals[m.MEDICAO];
          const medido = v.valor === '' || v.valor == null ? null : Number(v.valor);
          const ref = m.VALOR_REFERENCIA == null ? null : Number(m.VALOR_REFERENCIA);
          return {
            identificacao: m.MEDICAO,
            valor_referencia: ref,
            valor_medido: medido,
            // Desvio só faz sentido contra um alvo único; em faixa fica em branco.
            desvio: (medido != null && ref != null && m.VALOR_REFERENCIA_MAX == null) ? Number((medido - ref).toFixed(2)) : null,
            unidade: m.UNIDADE || null,
            resultado: v.resultado,
            ordem: i + 1,
          };
        })
      : (calibPreenchida ? [{
        identificacao: 'Calibração de temperatura',
        valor_referencia: calib.temp_programada === '' ? null : Number(calib.temp_programada),
        valor_medido: calib.temp_medida === '' ? null : Number(calib.temp_medida),
        desvio: calib.desvio === '' ? null : Number(calib.desvio),
        unidade: '°C',
        resultado: calib.aprovado ? 'Aprovado' : 'Reprovado',
      }] : []);
    return {
      finalizar,
      acoes: acoes.filter((a) => a.descricao?.trim()),
      // Só os campos que o backend grava (familia é apoio visual, fica de fora).
      pecas: pecas.map((p) => ({
        origem: p.origem === 'Central' ? 'Central' : 'Fora',
        id_catalogo: p.origem === 'Central' ? (p.id_catalogo || null) : null,
        codigo: p.codigo, fornecedor: p.fornecedor, descricao: p.descricao,
        quantidade: p.quantidade, motivo: p.motivo, valor: p.valor,
      })),
      limpeza, // lista fixa de atividades (item + feito)
      // Observação acompanha Pendente (o que falta) e Ajustado (o que foi feito).
      testes: testesOperacionais.filter((t) => testeRes[t]).map((t) => ({ teste: t, resultado: testeRes[t], observacao: (testeRes[t] === 'Pendente' || testeRes[t] === 'Ajustado') ? (testeObs[t] || null) : null })),
      calibracoes: calibracoesPayload,
      pendencias: pendencias.filter((p) => p.item?.trim() || p.recomendacao?.trim()),
      // Slots nomeados (Etiqueta pode vir vazia) + as fotos "Outros".
      fotos_depois: [
        ...Object.entries(fotosDepois).map(([slot, foto_url]) => ({ slot, foto_url })),
        ...fotosDepoisOutros.filter(Boolean).map((u, i) => ({ slot: `Outros ${i + 1}`, foto_url: u })),
      ],
      // Linha sem item ou sem valor é descartada aqui e no backend.
      versoes_sw: versoesSw.filter((v) => v.item?.trim() && v.valor?.trim()),
      parecer_tecnico: parecer || null,
      risco_especialista: riscoEsp ? 1 : 0,
      dias_servico: dias === '' ? null : Number(dias),
      aceite_nome: aceite.nome || null, aceite_data: aceite.data || null,
      reavaliacoes: Object.entries(reaval).map(([id_componente, v]) => ({ id_componente: Number(id_componente), classificacao: v.classificacao, observacao: v.observacao || null })),
    };
  };

  const salvar = async (finalizar) => {
    setErro(''); setResultado(null);
    // Itens que não se aplicam ao equipamento não são exibidos nem cobrados.
    const testesOperacionais = (base?.config?.testesOperacionais || []).filter((t) => !ausenteNoEquipamento(t));
    // Só os slots OBRIGATÓRIOS contam (Etiqueta/Patrimônio é opcional).
    const slotsDepoisObrig = base?.config?.slotsFotoDepoisObrigatorios || base?.config?.slotsFotoDepois || [];
    if (finalizar) {
      const faltam = testesOperacionais.filter((t) => !testeRes[t]);
      if (faltam.length > 0) { setErro(`Responda todos os testes para concluir: faltam ${faltam.join(', ')}.`); return; }
      // Medições: com lista no template, cada uma precisa de resultado (N/A
      // vale como resposta — a medição não se aplica). Sem lista, vale a
      // exigência histórica do bloco único de calibração.
      const medicoesTpl = (base?.config?.medicoesTemplate || []).filter((m) => !ausenteNoEquipamento(m.MEDICAO));
      if (medicoesTpl.length > 0) {
        const faltamMed = medicoesTpl.filter((m) => !medVals[m.MEDICAO]?.resultado).map((m) => m.MEDICAO);
        if (faltamMed.length > 0) { setErro(`Responda todas as medições para concluir: faltam ${faltamMed.join(', ')}.`); return; }
      } else if (calib.aprovado === null) {
        setErro('Informe o resultado da calibração (Aprovado/Reprovado).'); return;
      }
      const faltamFotos = slotsDepoisObrig.filter((s) => !fotosDepois[s]);
      if (faltamFotos.length > 0) { setErro(`Fotos DEPOIS obrigatórias faltando: ${faltamFotos.join(', ')}.`); return; }
    }
    setSalvando(true);
    setConcluindo(finalizar); // overlay de progresso só na conclusão (que gera o PDF)
    try {
      const r = await fetch(`${API}/etapa2/${idRelatorio}`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(montarPayloadEtapa2(finalizar)),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      if (finalizar) setResultado(data);
      else { setErro(''); await carregar(); alert('Rascunho da certificação salvo.'); }
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
      setConcluindo(false);
    }
  };

  // Autosave: salva o rascunho da Etapa 2 (finalizar=false) SEM recarregar (não pode
  // resetar o form no meio da edição) e SEM alerta. Lança em erro (o hook mostra o estado).
  const salvarRascunhoAuto = async () => {
    const r = await fetch(`${API}/etapa2/${idRelatorio}`, {
      method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(montarPayloadEtapa2(false)),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data?.message || 'Erro ao salvar rascunho.');
  };

  // Snapshot do conteúdo (string) — o autosave só dispara quando isto muda. Como o
  // hook usa baseline, ABRIR a tela não grava nada (não transiciona 'Em diagnóstico'
  // → 'Em execução' sem o usuário editar); só edições reais salvam.
  const snapshot = useMemo(
    () => JSON.stringify({ acoes, pecas, limpeza, testeRes, testeObs, calib, pendencias, fotosDepois, fotosDepoisOutros, parecer, riscoEsp, dias, aceite, reaval }),
    [acoes, pecas, limpeza, testeRes, testeObs, calib, pendencias, fotosDepois, fotosDepoisOutros, parecer, riscoEsp, dias, aceite, reaval],
  );
  const autosaveAtivo = !carregando && !!base && !resultado && !salvando;
  const { estado: autosaveEstado } = useAutosaveRascunho({ ativo: autosaveAtivo, snapshot, salvarFn: salvarRascunhoAuto });

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando…</p>;
  if (!base) return (
    <div><button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ marginTop: 12, padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erro || 'Relatório indisponível.'}</div></div>
  );

  if (resultado) {
    return (
      <div>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 28, textAlign: 'center' }}>
          <Award size={44} color={corResultado(resultado.resultadoCertificacao)} />
          <h3 style={{ margin: '12px 0 4px', color: '#0f172a' }}>Certificação concluída</h3>
          <div style={{ fontSize: 22, fontWeight: 800, color: corResultado(resultado.resultadoCertificacao), marginTop: 8 }}>
            {resultado.resultadoCertificacao}
          </div>
          <div style={{ marginTop: 6, color: '#64748b' }}>
            Índice de Saúde {resultado.indiceSaude}% · {resultado.classificacaoSaude}
            {resultado.dataValidade && <> · validade até {resultado.dataValidade}</>}
          </div>
          {resultado.temBloqueanteCritico && (
            <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontWeight: 700 }}>
              <ShieldAlert size={16} /> Item bloqueante Crítico no diagnóstico.
            </div>
          )}
        </div>
      </div>
    );
  }

  const r = base.relatorio;
  const testesOperacionaisTodos = base.config?.testesOperacionais || [];
  // Quem pode ser marcado N/A. Sem lista própria (fritadeira), todos podem —
  // é como sempre funcionou. Com lista própria (chapa), só os que o template
  // autoriza: senão o N/A viraria atalho para não responder um obrigatório.
  const testesNA = base.config?.testesQuePermitemNA || [];
  const permiteNA = (t) => (base.config?.testesVemDoTemplate ? testesNA.includes(t) : true);
  // Medições declaradas na versão. Vazio = fritadeira (bloco único de calibração).
  const medicoesTemplateTodas = base.config?.medicoesTemplate || [];
  // O que o equipamento REALMENTE tem, pelo cabeçalho gravado no diagnóstico:
  // numa chapa elétrica de 2 platens, as medições do Platen CENTRAL e o teste de
  // gás não são exibidos (mesma regra da Etapa 1 — ver blocosAusentes.js).
  // Não são gravados: medição ausente simplesmente não vira linha, e teste
  // ausente sai da lista de esperados.
  const medicoesTemplate = medicoesTemplateTodas.filter((m) => !ausenteNoEquipamento(m.MEDICAO));
  const medicoesOcultas = medicoesTemplateTodas.length - medicoesTemplate.length;
  const testesOperacionais = testesOperacionaisTodos.filter((t) => !ausenteNoEquipamento(t));
  const testesOcultos = testesOperacionaisTodos.length - testesOperacionais.length;
  // Só reavaliam os componentes que saíram Desgaste/Crítico no diagnóstico.
  const reavaliaveis = (base.componentes || []).filter((c) => c.CLASSIFICACAO < 2);

  return (
    <div>
      {concluindo && <OverlayProgresso titulo="Concluindo certificação…" subtitulo="Gerando o certificado em PDF — pode levar alguns segundos." />}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Certificação · {r.EQUIPAMENTO} · {r.LOJA} · #{r.ID}</h3>
        {/* Estado do autosave de rascunho da Etapa 2. */}
        {autosaveAtivo && (
          <span style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
            color: autosaveEstado === 'erro' ? '#b91c1c' : autosaveEstado === 'salvando' ? '#64748b' : '#059669',
          }}>
            {autosaveEstado === 'salvando' ? <><Cloud size={14} /> Salvando rascunho…</>
              : autosaveEstado === 'salvo' ? <><Check size={14} /> Rascunho salvo</>
                : autosaveEstado === 'erro' ? <><AlertTriangle size={14} /> Falha ao salvar rascunho</>
                  : <><Cloud size={14} /> Rascunho automático ligado</>}
          </span>
        )}
      </div>

      {/* Resumo do diagnóstico (Etapa 1) */}
      <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Diagnóstico</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: corSaude(r.CLASSIFICACAO_SAUDE) }}>{r.INDICE_SAUDE}% · {r.CLASSIFICACAO_SAUDE}</div>
        </div>
        {r.INDICE_SAUDE_POS != null && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Pós-conserto</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: corSaude(r.CLASSIFICACAO_SAUDE_POS) }}>{r.INDICE_SAUDE_POS}% · {r.CLASSIFICACAO_SAUDE_POS}</div>
          </div>
        )}
        <div style={{ fontSize: 13, color: '#64748b' }}>{base.naoConformidades.length} não conformidade(s) no diagnóstico · status atual: <strong>{r.STATUS}</strong></div>
      </div>

      {/* Erro de validação / salvamento aparece como POP-UP (modal) — antes era uma
          faixa inline que passava batida quando o usuário estava no fim do formulário. */}
      {erro && (
        <div onClick={() => setErro('')} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          {/* stopPropagation: clicar dentro do card não fecha; só o fundo/botão fecham. */}
          <div onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true" style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 440,
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ width: 48, height: 48, margin: '0 auto 12px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={26} color="#b91c1c" />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Não foi possível salvar</h3>
            <p style={{ margin: '0 0 20px', color: '#475569', fontSize: 14, lineHeight: 1.5 }}>{erro}</p>
            <button onClick={() => setErro('')} style={{
              padding: '10px 28px', background: '#4f46e5', color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 14,
            }}>Entendi</button>
          </div>
        </div>
      )}

      {/* Reavaliação pós-conserto — só itens Desgaste/Crítico do diagnóstico.
          É o que destrava (ou não) um bloqueante: marcar Conforme/Desgaste no
          item consertado tira o Crítico do cálculo do resultado. */}
      {reavaliaveis.length > 0 && (
        <Secao
          titulo="Reavaliação pós-conserto"
          descricao={'Reclassifique abaixo o estado de cada componente APÓS o reparo. Um item bloqueante que continuar Crítico reprova a certificação; reavaliá-lo para Conforme/Desgaste (depois de consertado/substituído) o destrava.'}
        >
          <div style={{ display: 'grid', gap: 8 }}>
            {reavaliaveis.map((c) => (
              <div key={c.ID_COMPONENTE} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 8, border: '1px solid #f1f5f9', borderRadius: 8 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
                    {c.COMPONENTE}{c.BLOQUEANTE ? <span style={{ color: '#b91c1c', fontWeight: 800 }}> (bloqueante)</span> : null}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {c.SISTEMA} · Diagnóstico: <strong style={{ color: c.CLASSIFICACAO === 0 ? '#b91c1c' : '#b45309' }}>{CLASSIF_LABEL[c.CLASSIFICACAO]}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {CLASSIF_OPCOES.map((o) => {
                    const sel = reaval[c.ID_COMPONENTE]?.classificacao === o.v;
                    return (
                      <button key={o.v} onClick={() => setReaval((m) => ({ ...m, [c.ID_COMPONENTE]: { ...m[c.ID_COMPONENTE], classificacao: o.v } }))} style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: sel ? `2px solid ${o.c}` : '1px solid #cbd5e1', background: sel ? o.bg : '#fff', color: sel ? o.c : '#64748b',
                      }}>{o.t}</button>
                    );
                  })}
                </div>
                <input style={{ ...inputStyle, flexBasis: '100%' }} placeholder="Observação (o que foi feito neste componente)"
                  value={reaval[c.ID_COMPONENTE]?.observacao || ''}
                  onChange={(e) => setReaval((m) => ({ ...m, [c.ID_COMPONENTE]: { ...m[c.ID_COMPONENTE], observacao: e.target.value } }))} />
              </div>
            ))}
          </div>
        </Secao>
      )}

      {/* Ações executadas */}
      <Secao titulo="Ações executadas" acao={btnAdd(() => setAcoes((a) => [...a, { descricao: '' }]), 'Ação')}>
        {acoes.length === 0 ? <Vazio /> : acoes.map((a, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input style={inputStyle} placeholder="Descrição da ação" value={a.descricao}
              onChange={(e) => setAcoes((arr) => arr.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
            {btnDel(() => setAcoes((arr) => arr.filter((_, j) => j !== i)))}
          </div>
        ))}
      </Secao>

      {/* Peças substituídas — mesmo seletor de origem da Etapa 1 ("Peças a comprar"),
          acrescido do R$ (o valor é CONFIRMADO aqui, na certificação).
          Origem: 'Central' = escolhida no catálogo da Central de Pedidos
          (código/descrição/fornecedor preenchem sozinhos); 'Fora' = digitada à mão. */}
      <Secao titulo="Peças substituídas" sub="R$" acao={btnAdd(() => setPecas((p) => [...p, { origem: 'Central', id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '', quantidade: 1, motivo: '', valor: '' }]), 'Peça')}>
        {pecas.length === 0 ? <Vazio /> : pecas.map((p, i) => {
          const setP = (patch) => setPecas((arr) => arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          const remover = btnDel(() => setPecas((arr) => arr.filter((_, j) => j !== i)));
          return (
            <div key={i} style={{ border: '1px solid #f1f5f9', borderRadius: 8, padding: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Origem:</span>
                {[['Central', 'Central de Pedidos'], ['Fora', 'Fora da central']].map(([o, rotulo]) => {
                  const sel = p.origem === o;
                  return (
                    <button key={o} onClick={() => {
                      // Trocar a origem zera a peça — não vaza dado do catálogo pro modo manual (e vice-versa).
                      if (!sel) setP({ origem: o, id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '', valor: '' });
                    }} style={{
                      padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: sel ? '2px solid #4338ca' : '1px solid #cbd5e1',
                      background: sel ? '#e0e7ff' : '#fff', color: sel ? '#4338ca' : '#64748b',
                    }}>{rotulo}</button>
                  );
                })}
              </div>

              {/* Central, peça ainda não escolhida → listas suspensas Fornecedor → Família → Peça */}
              {p.origem === 'Central' && !p.id_catalogo && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <SelecaoPecaCentral onEscolher={(r) => setP({
                    id_catalogo: r.ID, codigo: r.CODIGO, descricao: r.DESCRICAO,
                    fornecedor: r.FORNECEDOR, familia: r.FAMILIA || '',
                  })} />
                  {remover}
                </div>
              )}

              {/* Central, peça escolhida → código/fornecedor/família/descrição travados
                  (snapshot do catálogo) + quantidade e R$ editáveis */}
              {p.origem === 'Central' && p.id_catalogo && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '90px 110px 120px 1fr 60px 80px 32px', gap: 6, alignItems: 'end' }}>
                    <div><span style={labelStyle}>Código</span><input style={inputTravado} readOnly value={p.codigo || ''} /></div>
                    <div><span style={labelStyle}>Fornecedor</span><input style={inputTravado} readOnly value={p.fornecedor || ''} /></div>
                    <div><span style={labelStyle}>Família</span><input style={inputTravado} readOnly value={p.familia || ''} /></div>
                    <div><span style={labelStyle}>Descrição</span><input style={inputTravado} readOnly value={p.descricao || ''} /></div>
                    <div><span style={labelStyle}>Qtd</span>
                      <input style={inputStyle} type="number" min="1" placeholder="Qtd" value={p.quantidade} onChange={(e) => setP({ quantidade: e.target.value })} /></div>
                    <div><span style={labelStyle}>R$</span>
                      <input style={inputStyle} type="number" step="0.01" placeholder="R$" value={p.valor} onChange={(e) => setP({ valor: e.target.value })} /></div>
                    {remover}
                  </div>
                  <button onClick={() => setP({ id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '' })}
                    style={{ marginTop: 4, padding: '2px 8px', background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    trocar peça
                  </button>
                </>
              )}

              {/* Fora da central → digitação livre */}
              {p.origem !== 'Central' && (
                <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '100px 130px 1fr 60px 90px 32px', gap: 6, alignItems: 'center' }}>
                  {/* Código é opcional (rótulo deixa isso claro); Fornecedor é a informação nova. */}
                  <input style={inputStyle} placeholder="Código (opcional)" value={p.codigo} onChange={(e) => setP({ codigo: e.target.value })} />
                  <input style={inputStyle} placeholder="Fornecedor" value={p.fornecedor || ''} onChange={(e) => setP({ fornecedor: e.target.value })} />
                  <input style={inputStyle} placeholder="Descrição" value={p.descricao} onChange={(e) => setP({ descricao: e.target.value })} />
                  <input style={inputStyle} type="number" placeholder="Qtd" value={p.quantidade} onChange={(e) => setP({ quantidade: e.target.value })} />
                  <input style={inputStyle} type="number" step="0.01" placeholder="R$" value={p.valor} onChange={(e) => setP({ valor: e.target.value })} />
                  {remover}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Motivo:</span>
                {MOTIVOS_PECA.map((m) => {
                  const sel = p.motivo === m;
                  return (
                    <button key={m} onClick={() => setP({ motivo: m })} style={{
                      padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: sel ? '2px solid #4338ca' : '1px solid #cbd5e1',
                      background: sel ? '#e0e7ff' : '#fff', color: sel ? '#4338ca' : '#64748b',
                    }}>{m}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {pecas.length > 0 && (
          <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 800, color: '#0f172a' }}>
            Total: R$ {pecas.reduce((s, p) => s + (Number(p.valor) || 0) * (Number(p.quantidade) || 1), 0).toFixed(2)}
          </div>
        )}
      </Secao>

      {/* Limpeza técnica — atividades FIXAS, marcar Sim/Não */}
      <Secao titulo="Limpeza técnica">
        <div style={{ display: 'grid', gap: 6 }}>
          {limpeza.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ flex: 1, fontSize: 14, color: '#0f172a' }}>{l.item}</span>
              {[{ v: true, t: 'Sim', c: '#047857', bg: '#d1fae5' }, { v: false, t: 'Não', c: '#b91c1c', bg: '#fee2e2' }].map((o) => {
                const sel = l.feito === o.v;
                return (
                  <button key={o.t} onClick={() => upd(setLimpeza, i, 'feito', o.v)} style={{
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: sel ? `2px solid ${o.c}` : '1px solid #cbd5e1', background: sel ? o.bg : '#fff', color: sel ? o.c : '#64748b',
                  }}>{o.t}</button>
                );
              })}
            </div>
          ))}
        </div>
      </Secao>

      {/* Testes de certificação (6 fixos + por cuba existente, vindos do backend).
          Agrupados: "Gerais" (fixos) e cada "Resistência Cuba N". Ao marcar
          'Pendente' (antes 'Reprovado'), abre um campo de observação. */}
      <Secao titulo="Testes de certificação" sub={`${testesOperacionais.length} testes`}>
        {testesOcultos > 0 && (
          <div style={{ padding: '8px 12px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, marginBottom: 10, fontSize: 12, color: '#3730a3' }}>
            {testesOcultos} teste(s) não se aplicam a este equipamento e foram omitidos.
          </div>
        )}
        {(() => {
          // Agrupa pelo prefixo antes de ' · ' (nome da cuba); os fixos (sem ' · ')
          // ficam no grupo "Gerais". Preserva a ordem vinda do backend.
          const grupos = [];
          const idxGrupo = new Map();
          testesOperacionais.forEach((t) => {
            const p = t.indexOf(' · ');
            const g = p === -1 ? 'Gerais' : t.slice(0, p);
            if (!idxGrupo.has(g)) { idxGrupo.set(g, grupos.length); grupos.push({ nome: g, itens: [] }); }
            grupos[idxGrupo.get(g)].itens.push(t);
          });
          return grupos.map((grp) => (
            <div key={grp.nome} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, margin: '4px 0 6px' }}>{grp.nome}</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {grp.itens.map((t) => {
                  // Em grupo de cuba, mostra só o nome do componente (tira o prefixo "Cuba N · ").
                  const rotulo = grp.nome === 'Gerais' ? t : t.slice(grp.nome.length + 3);
                  return (
                    <div key={t} style={{ display: 'grid', gap: 6, padding: '6px 8px', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, fontSize: 14, color: '#0f172a' }}>{rotulo}</span>
                        {/* 'Ajustado' = estava fora e foi corrigido na visita. NÃO
                            derruba a certificação (conta como aprovado); existe para
                            deixar o rastro da intervenção no histórico.
                            N/A = teste não se aplica — sai da checagem "todos
                            aprovados". Com lista de testes própria (chapa), só os
                            marcados no template aceitam N/A; no fallback da
                            fritadeira todos aceitam, como sempre foi. */}
                        {/* Grade fixa de 4 colunas: a 4ª fica reservada e vazia
                            quando o teste não aceita N/A, para que as linhas
                            fiquem alinhadas independente disso. */}
                        <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? 'repeat(2, 1fr)' : `repeat(4, ${LARGURA_OPCAO}px)`, gap: 6 }}>
                          {['Aprovado', 'Ajustado', 'Pendente'].map((res) => (
                            <button key={res} onClick={() => setTesteRes((m) => ({ ...m, [t]: res }))}
                              style={estiloOpcao(testeRes[t] === res, res)}>
                              {res}
                            </button>
                          ))}
                          {permiteNA(t) ? (
                            <button onClick={() => setTesteRes((m) => ({ ...m, [t]: 'N/A' }))}
                              style={estiloOpcao(testeRes[t] === 'N/A', 'N/A')}>
                              N/A
                            </button>
                          ) : <span aria-hidden="true" />}
                        </div>
                      </div>
                      {/* Observação em Pendente (o que falta) e em Ajustado (o que
                          foi feito) — é o registro da intervenção em campo. */}
                      {(testeRes[t] === 'Pendente' || testeRes[t] === 'Ajustado') && (
                        <input style={inputStyle}
                          placeholder={testeRes[t] === 'Ajustado' ? 'Observação: o que foi ajustado?' : 'Observação: o que está pendente?'}
                          value={testeObs[t] || ''}
                          onChange={(e) => setTesteObs((m) => ({ ...m, [t]: e.target.value }))} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ));
        })()}
      </Secao>

      {/* Medições declaradas na versão do template (chapa): uma linha por
          medição, com a referência do fabricante ao lado do valor encontrado.
          Marcar N/A não grava a linha — a medição simplesmente não se aplica
          àquele equipamento (ex.: platen que não existe). */}
      {medicoesTemplate.length > 0 && (
        <Secao titulo="Medições" sub={`${medicoesTemplate.length} medições`}>
          {medicoesOcultas > 0 && (
            <div style={{ padding: '8px 12px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, marginBottom: 10, fontSize: 12, color: '#3730a3' }}>
              {medicoesOcultas} medição(ões) de bloco que este equipamento não possui foram omitidas
              {base.relatorio?.QTD_PLATENS ? ` (${base.relatorio.QTD_PLATENS} platen(s))` : ''}.
            </div>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {medicoesTemplate.map((m) => {
              const v = medVals[m.MEDICAO] || {};
              const un = m.UNIDADE || '';
              // Rótulo da referência: alvo único, faixa ("6 a 8") ou nada.
              const ref = m.VALOR_REFERENCIA == null ? null
                : m.VALOR_REFERENCIA_MAX == null ? `${Number(m.VALOR_REFERENCIA)} ${un}`.trim()
                  : `${Number(m.VALOR_REFERENCIA)} a ${Number(m.VALOR_REFERENCIA_MAX)} ${un}`.trim();
              return (
                <div key={m.MEDICAO} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 8px', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                  <span style={{ flex: '1 1 220px', fontSize: 14, color: '#0f172a' }}>
                    {m.MEDICAO}
                    {ref && <span style={{ color: '#64748b', fontSize: 12 }}> · ref. {ref}</span>}
                  </span>
                  <input type="number" style={{ ...inputStyle, width: 110, fontVariantNumeric: 'tabular-nums' }}
                    placeholder={un || 'valor'} value={v.valor ?? ''}
                    disabled={v.resultado === 'N/A'}
                    onChange={(e) => setMedVals((s) => ({ ...s, [m.MEDICAO]: { ...s[m.MEDICAO], valor: e.target.value } }))} />
                  {/* Mesma grade fixa dos testes — 4 colunas, a última reservada
                      para o N/A (vazia quando a medição não o aceita). */}
                  <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? 'repeat(2, 1fr)' : `repeat(4, ${LARGURA_OPCAO}px)`, gap: 6 }}>
                    {['Aprovado', 'Ajustado', 'Reprovado'].map((res) => (
                      <button key={res} onClick={() => setMedVals((s) => ({ ...s, [m.MEDICAO]: { ...s[m.MEDICAO], resultado: res } }))}
                        style={estiloOpcao(v.resultado === res, res)}>
                        {res}
                      </button>
                    ))}
                    {m.PERMITE_NA ? (
                      <button onClick={() => setMedVals((s) => ({ ...s, [m.MEDICAO]: { ...s[m.MEDICAO], resultado: 'N/A' } }))}
                        style={estiloOpcao(v.resultado === 'N/A', 'N/A')}>
                        N/A
                      </button>
                    ) : <span aria-hidden="true" />}
                  </div>
                </div>
              );
            })}
          </div>
        </Secao>
      )}

      {/* Versões registradas — software/placas lidos no equipamento. É RASTRO:
          não entra no Índice de Saúde nem na decisão de certificar; serve para a
          próxima visita saber qual firmware estava rodando. Linhas livres de
          propósito — não há o que validar num texto lido do display. */}
      <Secao titulo="Versões registradas" sub="opcional">
        <div style={{ display: 'grid', gap: 6 }}>
          {versoesSw.map((v, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={{ ...inputStyle, flex: '1 1 220px' }} placeholder="Informação (ex.: Placa de interface)"
                value={v.item}
                onChange={(e) => setVersoesSw((s) => s.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)))} />
              <select style={{ ...inputStyle, width: 150 }} value={v.posicao}
                onChange={(e) => setVersoesSw((s) => s.map((x, j) => (j === i ? { ...x, posicao: e.target.value } : x)))}>
                <option value="">Equipamento</option>
                <option value="ESQUERDO">Platen ESQUERDO</option>
                <option value="CENTRAL">Platen CENTRAL</option>
                <option value="DIREITO">Platen DIREITO</option>
              </select>
              <input style={{ ...inputStyle, width: 150 }} placeholder="Versão (ex.: v2.14b)"
                value={v.valor}
                onChange={(e) => setVersoesSw((s) => s.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)))} />
              <button onClick={() => setVersoesSw((s) => s.filter((_, j) => j !== i))}
                style={{ padding: '6px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                Remover
              </button>
            </div>
          ))}
          <button onClick={() => setVersoesSw((s) => [...s, { item: '', posicao: '', valor: '' }])}
            style={{ padding: '6px 12px', background: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', justifySelf: 'start' }}>
            + Versão
          </button>
        </div>
      </Secao>

      {/* Calibração — bloco único fixo. Só nas versões SEM medições declaradas
          (fritadeira); nas outras, a seção "Medições" acima é quem manda. */}
      {medicoesTemplate.length === 0 && (
      <Secao titulo="Calibração">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Temperatura Programada (°C)</label>
            <input style={{ ...inputStyle, marginTop: 4 }} type="number" value={calib.temp_programada}
              onChange={(e) => { const v = e.target.value; setCalib((c) => ({ ...c, temp_programada: v, desvio: calcDesvio(v, c.temp_medida) })); }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Temperatura Medida (°C)</label>
            <input style={{ ...inputStyle, marginTop: 4 }} type="number" value={calib.temp_medida}
              onChange={(e) => { const v = e.target.value; setCalib((c) => ({ ...c, temp_medida: v, desvio: calcDesvio(c.temp_programada, v) })); }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Desvio Encontrado (°C)</label>
            <input style={{ ...inputStyle, marginTop: 4 }} type="number" value={calib.desvio}
              onChange={(e) => setCalib((c) => ({ ...c, desvio: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Resultado:</span>
          {[{ v: true, t: 'Aprovado', c: '#047857', bg: '#d1fae5' }, { v: false, t: 'Reprovado', c: '#b91c1c', bg: '#fee2e2' }].map((o) => {
            const sel = calib.aprovado === o.v;
            return (
              <button key={o.t} onClick={() => setCalib((c) => ({ ...c, aprovado: o.v }))} style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: sel ? `2px solid ${o.c}` : '1px solid #cbd5e1', background: sel ? o.bg : '#fff', color: sel ? o.c : '#64748b',
              }}>{o.t}</button>
            );
          })}
        </div>
      </Secao>
      )}

      {/* Pendências não críticas */}
      <Secao titulo="Pendências não críticas" acao={btnAdd(() => setPendencias((p) => [...p, { item: '', recomendacao: '', prazo: '' }]), 'Pendência')}>
        {pendencias.length === 0 ? <Vazio /> : pendencias.map((p, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '1fr 1fr 120px 32px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <input style={inputStyle} placeholder="Item" value={p.item} onChange={(e) => upd(setPendencias, i, 'item', e.target.value)} />
            <input style={inputStyle} placeholder="Recomendação" value={p.recomendacao} onChange={(e) => upd(setPendencias, i, 'recomendacao', e.target.value)} />
            <input style={inputStyle} placeholder="Prazo" value={p.prazo} onChange={(e) => upd(setPendencias, i, 'prazo', e.target.value)} />
            {btnDel(() => setPendencias((arr) => arr.filter((_, j) => j !== i)))}
          </div>
        ))}
      </Secao>

      {/* Fotos DEPOIS — mesma regra do ANTES: quantas são obrigatórias depende
          do EQUIPAMENTO, e o subtítulo conta a lista que veio do servidor. */}
      <Secao titulo="Registro fotográfico — DEPOIS" sub={`${(base.config?.slotsFotoDepoisObrigatorios || base.config?.slotsFotoDepois || []).length} obrigatórias + opcionais`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {(base.config?.slotsFotoDepois || []).map((slot) => {
            const obrigatorio = (base.config?.slotsFotoDepoisObrigatorios || base.config?.slotsFotoDepois || []).includes(slot);
            return (
              <div key={slot} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 8, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#475569' }}>
                  {slot}{!obrigatorio && <span style={{ color: '#94a3b8', fontWeight: 400 }}> (opcional)</span>}
                </span>
                <FotoUpload url={fotosDepois[slot]} label={fotosDepois[slot] ? 'OK' : 'Foto'} onUrl={(u) => setFotosDepois((f) => ({ ...f, [slot]: u }))}
                  patrimonio={base.relatorio.NUMERO_ATIVO} chave={base.relatorio.CHAVE_ARQUIVOS || `rel${base.relatorio.ID}`} slot={`depois_${slot}`} />
              </div>
            );
          })}
        </div>
        {/* Outros — até 3 fotos livres (opcionais) */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Outros <span style={{ color: '#94a3b8', fontWeight: 400 }}>(até 3, opcional)</span></span>
          <FotosOutros fotos={fotosDepoisOutros} onChange={setFotosDepoisOutros} max={3}
            patrimonio={base.relatorio.NUMERO_ATIVO} chave={base.relatorio.CHAVE_ARQUIVOS || `rel${base.relatorio.ID}`} prefixoSlot="depois_Outros" />
        </div>
      </Secao>

      {/* Parecer + risco + aceite */}
      <Secao 
          titulo="Parecer técnico e aceite" 
          descricao={`O equipamento foi submetido ao Programa de Certificação de Equipamentos do Grupo Correia, contemplando inspeção técnica, recuperação preventiva, substituição de componentes, limpeza técnica e testes operacionais.\nApós a conclusão das atividades descritas neste relatório, o equipamento encontra-se apto para operação dentro dos padrões estabelecidos pelo Grupo Correia.\n\nObservações Complementares:`}
        >
        <textarea style={{ ...inputStyle, minHeight: 70 }} placeholder="Parecer técnico" value={parecer} onChange={(e) => setParecer(e.target.value)} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
          <input type="checkbox" checked={riscoEsp} onChange={(e) => setRiscoEsp(e.target.checked)} />
          <ShieldAlert size={14} /> Risco apontado pelo especialista (reprova a certificação)
        </label>
        <div style={{ marginTop: 12, maxWidth: 220 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Dias de serviço (resumo executivo)</label>
          <input style={{ ...inputStyle, marginTop: 4 }} type="number" step="1" min="0" placeholder="ex.: 3" value={dias} onChange={(e) => setDias(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 12 }}>
          <input style={inputStyle} placeholder="Aceite — nome do gerente" value={aceite.nome} onChange={(e) => setAceite({ ...aceite, nome: e.target.value })} />
          <input style={inputStyle} type="date" value={aceite.data} onChange={(e) => setAceite({ ...aceite, data: e.target.value })} />
        </div>
      </Secao>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => salvar(true)} disabled={salvando} style={{ padding: '12px 24px', background: salvando ? '#94a3b8' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
          <CheckCircle2 size={18} /> {salvando ? 'Processando…' : 'Concluir certificação'}
        </button>
      </div>
    </div>
  );
};

// Helpers de edição de lista (setter, índice, campo, valor)
function upd(setter, i, campo, valor) {
  setter((arr) => arr.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));
}
function calcDesvio(prog, med) {
  if (prog === '' || med === '' || prog == null || med == null) return '';
  const d = Number(med) - Number(prog);
  return Number.isFinite(d) ? d : '';
}
const Vazio = () => <p style={{ color: '#94a3b8', margin: 0, fontSize: 13 }}>Nenhum item.</p>;

export default CertificacaoCertificar;
